import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  useTheme,
  Button,
  TextField,
  Autocomplete,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { GET, POST, PUT } from "../Generic/CrudRequest";
import Icons from "../../Utils/Icons";
import { MadrinaEdit, MadrinaAdd } from "./MadrinaAddOrEdit";
import { useTranslation } from "react-i18next";

export default function AddOrEditMadrina({ config }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [madrinaData, setMadrinaData] = useState(new MadrinaAdd());
  const [locations, setLocations] = useState([]);
  const [locationMadrina, setLocationMadrina] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (location.pathname.includes("/Madrinas/Editar")) {
      setTitle(t("madrinas.edit"));
      const locationId = getLocationIdFromPath(location.pathname);
      getMadrinaById(locationId);
    } else {
      setTitle(t("madrinas.add"));
    }
  }, [location.pathname, t]);

  // Escuchar cambios de idioma para actualizar el título
  useEffect(() => {
    const handleLanguageChange = () => {
      if (location.pathname.includes("/Madrinas/Editar")) {
        setTitle(t("madrinas.edit"));
      } else {
        setTitle(t("madrinas.add"));
      }
    };

    // Agregar listener para el evento de cambio de idioma
    window.addEventListener('languageChanged', handleLanguageChange);

    // Cleanup: remover listener cuando el componente se desmonte
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [t, location.pathname]);

  useEffect(() => {
    if (title === t("madrinas.edit") && madrinaData.idUbicacion !== 0) {
      setLocationMadrina({
        idUbicacion: madrinaData.idUbicacion,
        nombre: madrinaData.ubicacion,
        descripcion: "",
        ciudad: "",
        ubicacionTipo: "",
      });
    }

    if (title === t("madrinas.edit") && madrinaData.idEmpleado !== 0) {
      setEmployee({
        idEmpleado: madrinaData.idEmpleado,
        nombre: madrinaData.nombre,
        noEmpleado: madrinaData.noEmpleado,
      });
    }
  }, [title, madrinaData, t]);

  const getLocationIdFromPath = (pathname) => {
    const parts = pathname.split("/");
    return parts[parts.length - 1];
  };

  const getMadrinaById = async (madrinaId) => {
    const dataMadrina = await GET(`${config.url}/api/Truck/${madrinaId}`);
    const updateDataMadrina = new MadrinaEdit(dataMadrina);

    setMadrinaData(updateDataMadrina);

    if (updateDataMadrina.idUbicacion !== 0) {
      const locationData = await GET(
        `${config.url}/api/Location/${updateDataMadrina.idUbicacion}`
      );
      fetchLocations(locationData.nombre, 1, 1);
    }
  };

  const fetchLocations = async (location, currentPage, perPage) => {
    const dataLocations = await GET(`${config.url}/api/Location/select`, {
      busqueda: location,
      page: currentPage,
      perPage: perPage,
    });

    setLocations(dataLocations.result);
  };

  const fetchEmployees = async (searchText) => {
    const responseEmployes = await GET(
      `${config.url}/api/Employes/buscar/${searchText}`
    );
    setEmployees(responseEmployes);
  };

  const addMadrina = async () => {
    const requiredFields = ["numeroEconomico", "numeroTrailer"];
    const newErrors = {};

    if (madrinaData.idUbicacion === 0) {
      newErrors.ubicacion = t("madrinas.requiredField");
    }

    requiredFields.forEach((field) => {
      const fieldValue = getFieldByPath(madrinaData, field);
      if (!fieldValue || fieldValue === "") {
        newErrors[field] = t("madrinas.requiredField");
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    await POST(`${config.url}/api/Truck`, madrinaData);
    navigate("/Madrinas");
  };

  const editMadrina = async () => {
    const requiredFields = ["numeroEconomico", "numeroTrailer"];
    const newErrors = {};

    if (madrinaData.idUbicacion === 0) {
      newErrors.ubicacion = t("madrinas.requiredField");
    }

    requiredFields.forEach((field) => {
      const fieldValue = getFieldByPath(madrinaData, field);
      if (!fieldValue || fieldValue === "") {
        newErrors[field] = t("madrinas.requiredField");
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    await PUT(`${config.url}/api/Truck`, madrinaData);
    navigate("/Madrinas");
  };

  const getFieldByPath = (object, path) => {
    const pathArray = path.split(".");
    let value = object;
    for (const prop of pathArray) {
      value = value[prop];
      if (value === undefined) break;
    }
    return value;
  };

  const handleSubmit = async () => {
    if (title === t("madrinas.add")) {
      await addMadrina();
    } else if (title === t("madrinas.edit")) {
      await editMadrina();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMadrinaData({
      ...madrinaData,
      [name]: value,
    });
  };

  const handleInputChange = (event, value) => {
    if (value.trim().length >= 3) {
      fetchLocations(value, 1, 10);
    } else {
      setLocations([]);
    }
  };

  const handleSearchChange = (event, value) => {
    if (value.trim().length >= 3) {
      fetchEmployees(value);
    } else {
      setEmployees([]);
    }
  };

  const handleNumberInput = (e) => {
    const key = e.key;

    if (
      !/^[0-9]$/.test(key) &&
      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(key)
    ) {
      e.preventDefault();
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: "hidden",
        boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: 2,
      }}
    >
      <CardContent>
        <CardContent
          sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
        >
          <Icons
            iconName="ArrowBack"
            fontSize="large"
            style={{
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              marginRight: 20,
            }}
            to="/Madrinas"
            tooltip={t("buttons.back")}
          />
          <Typography variant="h3" sx={{ flex: "1", fontWeight: "bold" }}>
            {title} {t("madrinas.title")}
          </Typography>
        </CardContent>
        <Divider sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }} />
        <Card
          variant="outlined"
          sx={{
            overflow: "hidden",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(0, 0, 0, 0.1)",
            borderRadius: 2,
            maxWidth: "calc(100% - 32px)",
            margin: "auto",
            marginTop: 2,
          }}
        >
          <CardContent sx={{ maxWidth: "calc(100% - 64px)", margin: "auto" }}>
            <Typography
              variant="h5"
              sx={{
                flex: "1",
                color: theme.palette.primary.main,
              }}
            >
              {t("madrinas.madrinaInfo")}
            </Typography>
            <Divider sx={{ maxWidth: "100%", marginTop: 2 }} />
            <Grid container spacing={2} sx={{ marginTop: 2 }}>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t("madrinas.vin")}
                  name="vin"
                  variant="standard"
                  fullWidth
                  onChange={handleChange}
                  value={madrinaData.vin || ""}
                  inputProps={{ maxLength: 17 }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t("madrinas.numeroEconomico")}
                  name="numeroEconomico"
                  variant="standard"
                  fullWidth
                  onChange={handleChange}
                  value={madrinaData.numeroEconomico || ""}
                  error={!!errors.numeroEconomico}
                  helperText={errors.numeroEconomico}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  options={locations}
                  noOptionsText={t("noOptions")}
                  value={locationMadrina}
                  getOptionLabel={(location) =>
                    `${location.nombre} ${location.descripcion} ${
                      location.ciudad ?? ""
                    } ${location.ubicacionTipo ?? ""}`
                  }
                  onChange={(event, value) => {
                    if (value) {
                      setLocationMadrina(value);
                      setMadrinaData({
                        ...madrinaData,
                        ubicacion: value.nombre,
                        idUbicacion: value.idUbicacion,
                      });
                    } else {
                      setMadrinaData({
                        ...madrinaData,
                        ubicacion: "",
                        idUbicacion: 0,
                      });
                    }
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option && value && option.idUbicacion === value.idUbicacion
                  }
                  onInputChange={handleInputChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("madrinas.ubicacion")}
                      variant="standard"
                      fullWidth
                      value={locationMadrina}
                      error={!!errors.ubicacion}
                      helperText={errors.ubicacion}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t("madrinas.placas")}
                  name="placas"
                  variant="standard"
                  fullWidth
                  onChange={handleChange}
                  value={madrinaData.placas || ""}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t("madrinas.placasUsa")}
                  name="placasUsa"
                  variant="standard"
                  fullWidth
                  onChange={handleChange}
                  value={madrinaData.placasUsa || ""}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t("madrinas.modelo")}
                  name="modelo"
                  variant="standard"
                  fullWidth
                  onChange={handleChange}
                  value={madrinaData.modelo || ""}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t("madrinas.anio")}
                  name="anio"
                  variant="standard"
                  fullWidth
                  onChange={handleChange}
                  onKeyDown={handleNumberInput}
                  value={madrinaData.anio || ""}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  options={employees}
                  noOptionsText={t("noOptions")}
                  getOptionLabel={(employee) =>
                    `${employee.noEmpleado} - ${employee.nombre} ${
                      employee.apellidoPaterno ?? ""
                    } ${employee.apellidoMaterno ?? ""}`
                  }
                  value={employee}
                  onChange={(event, value) => {
                    if (value) {
                      setMadrinaData({
                        ...madrinaData,
                        idEmpleado: value.idEmpleado,
                        noEmpleado: value.noEmpleado,
                        nombre: value.nombre,
                      });
                    } else {
                      setMadrinaData({
                        ...madrinaData,
                        noEmpleado: "",
                        nombre: "",
                      });
                    }
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option && value && option.idEmpleado === value.idEmpleado
                  }
                  onInputChange={handleSearchChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("madrinas.empleado")}
                      variant="standard"
                      fullWidth
                      value={employee}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t("madrinas.numeroTrailer")}
                  name="numeroTrailer"
                  variant="standard"
                  fullWidth
                  onChange={handleChange}
                  value={madrinaData.numeroTrailer || ""}
                  error={!!errors.numeroTrailer}
                  helperText={errors.numeroTrailer}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={t("madrinas.tipoTrailer")}
                  name="tipoTrailer"
                  variant="standard"
                  fullWidth
                  onChange={handleChange}
                  value={madrinaData.tipoTrailer || ""}
                />
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-end" sx={{ marginTop: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                {t("madrinas.save")}
              </Button>
            </Grid>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  );
}
